import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Clock,
  Mail,
  Users,
  Play,
  Pause,
  Trash2,
  Plus,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  FileText,
  Settings
} from 'lucide-react';
import { api } from '../auth/authStore';
import toast from 'react-hot-toast';

interface ScheduledEmail {
  id: string;
  name: string;
  type: string;
  format: string;
  frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly';
  recipients: string[];
  nextRun: string | null;
  isActive: boolean;
}

interface ScheduledEmailsManagementProps {
  onCreateNew?: () => void;
}

export const ScheduledEmailsManagement: React.FC<ScheduledEmailsManagementProps> = ({
  onCreateNew
}) => {
  const [scheduledEmails, setScheduledEmails] = useState<ScheduledEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchScheduledEmails = async () => {
    try {
      const response = await api.get('/firm-admin/reports/scheduled');
      if (response.data.success) {
        setScheduledEmails(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching scheduled emails:', error);
      toast.error('Failed to load scheduled emails');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScheduledEmails();
  }, []);

  const handleToggleActive = async (scheduleId: string, isActive: boolean) => {
    setActionLoading(scheduleId);
    try {
      if (isActive) {
        // Cancel the schedule
        await api.delete(`/firm-admin/reports/scheduled/${scheduleId}`);
        toast.success('Scheduled email cancelled');
      } else {
        // Reactivate would require a separate endpoint or recreation
        toast('To reactivate, please create a new schedule', { icon: 'ℹ️' });
      }
      
      await fetchScheduledEmails();
    } catch (error: any) {
      console.error('Error toggling schedule:', error);
      toast.error('Failed to update schedule');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (scheduleId: string) => {
    if (!confirm('Are you sure you want to delete this scheduled email? This action cannot be undone.')) {
      return;
    }

    setActionLoading(scheduleId);
    try {
      await api.delete(`/firm-admin/reports/scheduled/${scheduleId}`);
      toast.success('Scheduled email deleted');
      await fetchScheduledEmails();
    } catch (error: any) {
      console.error('Error deleting schedule:', error);
      toast.error('Failed to delete schedule');
    } finally {
      setActionLoading(null);
    }
  };

  const getFrequencyIcon = (frequency: string) => {
    switch (frequency) {
      case 'daily':
        return <Clock className="w-4 h-4" />;
      case 'weekly':
        return <Calendar className="w-4 h-4" />;
      case 'monthly':
        return <Calendar className="w-4 h-4" />;
      case 'quarterly':
        return <Calendar className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getFrequencyColor = (frequency: string) => {
    switch (frequency) {
      case 'daily':
        return 'text-green-600 bg-green-100';
      case 'weekly':
        return 'text-blue-600 bg-blue-100';
      case 'monthly':
        return 'text-purple-600 bg-purple-100';
      case 'quarterly':
        return 'text-orange-600 bg-orange-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatNextRun = (nextRun: string | null) => {
    if (!nextRun) return 'Not scheduled';
    
    const date = new Date(nextRun);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays < 7) return `In ${diffDays} days`;
    
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
        <span className="ml-2 text-lg">Loading scheduled emails...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Scheduled Email Deliveries</h3>
          <p className="text-sm text-gray-500">Manage automated report email deliveries</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchScheduledEmails}
            className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          {onCreateNew && (
            <button
              onClick={onCreateNew}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Schedule New
            </button>
          )}
        </div>
      </div>

      {/* Scheduled Emails List */}
      {scheduledEmails.length === 0 ? (
        <div className="text-center py-12">
          <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Scheduled Emails</h3>
          <p className="text-gray-500 mb-6">
            You haven't set up any automated email deliveries yet.
          </p>
          {onCreateNew && (
            <button
              onClick={onCreateNew}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors mx-auto"
            >
              <Plus className="w-4 h-4 mr-2" />
              Schedule Your First Email
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {scheduledEmails.map((schedule) => (
            <div
              key={schedule.id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <div className="flex items-center">
                      <FileText className="w-5 h-5 text-gray-400 mr-2" />
                      <h4 className="text-lg font-medium text-gray-900">{schedule.name}</h4>
                    </div>
                    <div className={`ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getFrequencyColor(schedule.frequency)}`}>
                      {getFrequencyIcon(schedule.frequency)}
                      <span className="ml-1 capitalize">{schedule.frequency}</span>
                    </div>
                    <div className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      schedule.isActive ? 'text-green-800 bg-green-100' : 'text-gray-800 bg-gray-100'
                    }`}>
                      {schedule.isActive ? (
                        <>
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Active
                        </>
                      ) : (
                        <>
                          <Pause className="w-3 h-3 mr-1" />
                          Inactive
                        </>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                    <div>
                      <span className="font-medium">Type:</span> {schedule.type}
                    </div>
                    <div>
                      <span className="font-medium">Format:</span> {schedule.format.toUpperCase()}
                    </div>
                    <div>
                      <span className="font-medium">Next Run:</span> {formatNextRun(schedule.nextRun)}
                    </div>
                  </div>

                  <div className="mt-3 flex items-center text-sm text-gray-600">
                    <Users className="w-4 h-4 mr-1" />
                    <span className="font-medium">Recipients:</span>
                    <span className="ml-1">
                      {schedule.recipients.length} recipient{schedule.recipients.length !== 1 ? 's' : ''}
                    </span>
                    <div className="ml-2 flex -space-x-1">
                      {schedule.recipients.slice(0, 3).map((recipient, index) => (
                        <div
                          key={index}
                          className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs font-medium text-gray-600 border-2 border-white"
                          title={recipient}
                        >
                          {recipient.charAt(0).toUpperCase()}
                        </div>
                      ))}
                      {schedule.recipients.length > 3 && (
                        <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center text-xs font-medium text-gray-600 border-2 border-white">
                          +{schedule.recipients.length - 3}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => handleToggleActive(schedule.id, schedule.isActive)}
                    disabled={actionLoading === schedule.id}
                    className={`p-2 rounded-lg transition-colors ${
                      schedule.isActive
                        ? 'text-orange-600 hover:bg-orange-50'
                        : 'text-green-600 hover:bg-green-50'
                    } disabled:opacity-50`}
                    title={schedule.isActive ? 'Pause schedule' : 'Resume schedule'}
                  >
                    {actionLoading === schedule.id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : schedule.isActive ? (
                      <Pause className="w-4 h-4" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDelete(schedule.id)}
                    disabled={actionLoading === schedule.id}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Delete schedule"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="ml-3">
            <h4 className="text-sm font-medium text-blue-900">Email Delivery Information</h4>
            <ul className="text-sm text-blue-700 mt-1 space-y-1">
              <li>• Scheduled emails are sent automatically based on the configured frequency</li>
              <li>• Reports are generated fresh for each scheduled delivery</li>
              <li>• Recipients will receive an email with the report attached</li>
              <li>• You can pause or cancel schedules at any time</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
